<template>
    <SmithsChartCard :has-data="hasData" :loading="loading" :title="title" :empty-text="emptyText">
        <template #sidebar>
            <SidebarKpi :label="kpiLabel" :value="hasData ? formatPercent(data?.overallPct) : '—'" />
            <SidebarKpi :label="`Days over ${data?.threshold ?? 0}%`" :value="hasData ? (data?.daysOverThreshold ?? 0) : '—'" />
        </template>
        <c-chart :key="componentKey" :custom-tooltips="false" :data="chartDataset" :options="chartOptions" class="h-full" type="line" />
    </SmithsChartCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CChart } from '@coreui/vue-chartjs';
import SmithsChartCard from './SmithsChartCard.vue';
import SidebarKpi from './SidebarKpi.vue';
import { getBaseChartOptions, chartColors, lightTooltip } from '@/Utils/chartDefaults';

const props = defineProps({
    title: { type: String, required: true },
    kpiLabel: { type: String, required: true },
    datasetLabel: { type: String, required: true },
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: 'No data available for this period.' },
});

const componentKey = ref(0);
const hasData = computed(() => props.data?.labels?.length > 0);

function formatPercent(value) {
    if (value == null) return '—';
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) + '%';
}

const chartDataset = computed(() => {
    if (!hasData.value) return { labels: [], datasets: [] };

    return {
        labels: props.data.labels,
        datasets: [
            {
                label: props.datasetLabel,
                data: props.data.values,
                backgroundColor: chartColors.primaryFill,
                borderColor: chartColors.primary,
                borderWidth: 1.5,
                fill: true,
                tension: 0.3,
                pointRadius: 0,
            },
        ],
    };
});

const chartOptions = computed(() => ({
    ...getBaseChartOptions(),
    interaction: { mode: 'index', intersect: false },
    scales: {
        x: {
            grid: { display: false },
            ticks: { font: { size: 11 } },
        },
        y: {
            beginAtZero: true,
            grid: { drawBorder: false },
            ticks: { callback: (value) => value + '%' },
        },
    },
    plugins: {
        ...getBaseChartOptions().plugins,
        annotation: {
            annotations: {
                threshold: {
                    type: 'line',
                    yMin: props.data?.threshold ?? 0,
                    yMax: props.data?.threshold ?? 0,
                    borderColor: chartColors.thresholdLine,
                    borderWidth: 1,
                    borderDash: [5, 5],
                },
            },
        },
        tooltip: {
            ...lightTooltip,
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
            },
        },
    },
}));
</script>
